/* app/providers/UnsavedChangesProvider.jsx — единый сторож «несохранённых изменений».
   Любой редактор (Drawer/форма) регистрирует «грязное» состояние через
   useUnsavedGuard(isDirty). При попытке закрыть/уйти — спрашивает confirm.
   API: const { guard, register } = useUnsaved();
        guard(action) — выполнит action, если чисто, иначе спросит.
   В S0 закладывает контракт; редакторы появятся на своих этапах. */

import React from 'react';
import { useConfirm } from './ConfirmProvider.jsx';

  const { createContext, useContext, useState, useCallback, useRef, useEffect } = React;

  const UnsavedContext = createContext(null);

  function UnsavedProvider({ children }) {
    const dirtyRef = useRef(new Set());
    const confirm = useConfirm();

    const register = useCallback((key, isDirty) => {
      if (isDirty) dirtyRef.current.add(key);
      else dirtyRef.current.delete(key);
    }, []);

    const isAnyDirty = useCallback(() => dirtyRef.current.size > 0, []);

    // guard(action): если есть грязные — спросить; да → очистить и выполнить
    const guard = useCallback(async (action) => {
      if (!dirtyRef.current.size) { action(); return true; }
      const ok = await confirm({
        title: 'Несохранённые изменения',
        body: 'Если закрыть сейчас, внесённые изменения будут потеряны.',
        confirmLabel: 'Закрыть без сохранения',
        cancelLabel: 'Остаться',
        tone: 'danger',
      });
      if (ok) { dirtyRef.current.clear(); action(); }
      return ok;
    }, [confirm]);

    // beforeunload — браузерное предупреждение при перезагрузке/закрытии вкладки
    useEffect(() => {
      const onBeforeUnload = (e) => {
        if (dirtyRef.current.size) { e.preventDefault(); e.returnValue = ''; }
      };
      window.addEventListener('beforeunload', onBeforeUnload);
      return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    const value = { register, guard, isAnyDirty };
    return <UnsavedContext.Provider value={value}>{children}</UnsavedContext.Provider>;
  }

  // хук для редактора: помечает себя грязным/чистым
  function useUnsavedGuard(key, isDirty) {
    const ctx = useContext(UnsavedContext);
    useEffect(() => {
      ctx && ctx.register(key, isDirty);
      return () => ctx && ctx.register(key, false);
    }, [key, isDirty]);
  }

  const useUnsaved = () => useContext(UnsavedContext);

export { UnsavedProvider };
export { useUnsaved };
export { useUnsavedGuard };
